/**
 * LeadsMap — field-sales map view powered by Google Maps JS API.
 * Clustered pins colored by sector, unified retractable control panel
 * (sector/legend/CRM filters/geo-radius), lead preview card, multi-stop
 * route builder with nearest-neighbor optimization and native Google Maps
 * hand-off ("Navegar Agora").
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { APIProvider, Map as GMap, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  Loader2, Map as MapIcon, ArrowLeft, SlidersHorizontal, X, Maximize2,
  Minimize2, Navigation, Route as RouteIcon, MapPin, Search, Lock,
  Trash2, Sparkles, ChevronDown, ChevronUp, Crosshair,
  HelpCircle, DollarSign, Mail, ExternalLink, Landmark, Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import BottomSheet, { useIsMobile } from "../components/BottomSheet";
import QuickActionPanel from "../components/QuickActionPanel";
import ExportButton from "../components/ExportButton";
import { useLang } from "../i18n/LangContext";
import { useTrialLock } from "../hooks/useTrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const SECTOR_COLORS = ["#C9A24E", "#3B82F6", "#10B981", "#A855F7", "#F59E0B", "#EF4444", "#0EA5E9", "#EC4899"];
// Reserved amber for Government leads — same swatch across map, legend and
// popup so contractors spot public bids at a glance.
const GOV_COLOR = "#F59E0B";
const STATUS_META = {
  NEW:       { pt: "Novo",         en: "New",        color: "#3B82F6" },
  CONTACTED: { pt: "Contatado",    en: "Contacted",  color: "#0EA5E9" },
  QUALIFIED: { pt: "Qualificado",  en: "Qualified",  color: "#A855F7" },
  PROPOSAL:  { pt: "Em Negociação", en: "Proposal",  color: "#F59E0B" },
  WON:       { pt: "Ganho",        en: "Won",        color: "#10B981" },
  LOST:      { pt: "Perdido",      en: "Lost",       color: "#9CA3AF" },
};
// Iter336 · Radius selectors expressed in MILES for US-based sales teams.
// Internally we still store `radiusKm` (backend uses km) — value is
// converted on selection: km = miles × 1.609.
const RADIUS_MILES = [0, 5, 10, 25, 50];
const MILE_TO_KM = 1.609344;

const distKm = (a, b) => {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Nearest-neighbor ordering from an optional start point
const optimizeOrder = (stops, start) => {
  const rest = [...stops];
  const ordered = [];
  let cur = start || rest.shift();
  if (!start) ordered.push(cur);
  while (rest.length) {
    let bi = 0, bd = Infinity;
    rest.forEach((s, i) => { const d = distKm(cur, s); if (d < bd) { bd = d; bi = i; } });
    cur = rest.splice(bi, 1)[0];
    ordered.push(cur);
  }
  return ordered;
};

function MapContent({ leads, colorFor, onSelect, routeIds, routePath, focusCenter }) {
  const map = useMap();
  const clusterRef = useRef(null);
  const polyRef = useRef(null);
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    const markers = leads.map((l) => {
      const inRoute = routeIds.has(l.id);
      const isGov = l.kind === "gov";
      const m = new g.Marker({
        position: { lat: l.lat, lng: l.lng },
        title: l.company_name || l.name,
        icon: isGov
          ? {
              // Amber diamond for Government pins — visually distinct from B2B dots.
              path: "M 0,-9 L 9,0 L 0,9 L -9,0 Z",
              scale: inRoute ? 1.2 : 1,
              fillColor: colorFor(l.sector, l),
              fillOpacity: 0.95,
              strokeColor: inRoute ? "#0A1233" : "#78350F",
              strokeWeight: inRoute ? 3 : 2,
            }
          : {
              path: g.SymbolPath.CIRCLE, scale: inRoute ? 10 : 8,
              fillColor: colorFor(l.sector, l), fillOpacity: 0.92,
              strokeColor: inRoute ? "#0A1233" : "#FFFFFF", strokeWeight: inRoute ? 3 : 2,
            },
      });
      m.addListener("click", () => onSelect(l));
      return m;
    });
    clusterRef.current = new MarkerClusterer({ map, markers });
    return () => { clusterRef.current?.clearMarkers(); clusterRef.current?.setMap?.(null); };
  }, [map, leads, routeIds, colorFor, onSelect]);

  // Fit bounds once when leads arrive
  const fitted = useRef(false);
  useEffect(() => {
    if (!map || !window.google?.maps || fitted.current || leads.length === 0) return;
    const b = new window.google.maps.LatLngBounds();
    leads.forEach((l) => b.extend({ lat: l.lat, lng: l.lng }));
    map.fitBounds(b, 60);
    fitted.current = true;
  }, [map, leads]);

  // Route polyline
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    polyRef.current?.setMap(null);
    if (routePath.length >= 2) {
      polyRef.current = new window.google.maps.Polyline({
        map, path: routePath, strokeColor: "#1B3A8A", strokeOpacity: 0.85,
        strokeWeight: 4, geodesic: true,
      });
    }
    return () => polyRef.current?.setMap(null);
  }, [map, routePath]);

  // Geo-filter radius circle
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    circleRef.current?.setMap(null);
    if (focusCenter?.radiusKm > 0) {
      circleRef.current = new window.google.maps.Circle({
        map, center: { lat: focusCenter.lat, lng: focusCenter.lng },
        radius: focusCenter.radiusKm * 1000,
        strokeColor: "#C9A24E", strokeWeight: 2, fillColor: "#C9A24E", fillOpacity: 0.08,
      });
      map.panTo({ lat: focusCenter.lat, lng: focusCenter.lng });
    }
    return () => circleRef.current?.setMap(null);
  }, [map, focusCenter]);

  return null;
}

export default function LeadsMap() {
  const isMobile = useIsMobile();
  const [quickAction, setQuickAction] = useState(null);
  const navigate = useNavigate();
  const { lang } = useLang();
  const isPt = lang === "pt";
  const { onTrial } = useTrialLock();
  const [items, setItems] = useState([]);
  const [sectorCatalog, setSectorCatalog] = useState([]); // [{sector, count}]
  const [loading, setLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const [focus, setFocus] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [selected, setSelected] = useState(null);
  const [route, setRoute] = useState([]);

  // Filters
  // Multi-sector selection — Set<string>. Empty = show all.
  const [sectorFilter, setSectorFilter] = useState(() => new Set());
  const [sectorMenuOpen, setSectorMenuOpen] = useState(false);
  const toggleSector = (name) => setSectorFilter((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });
  const clearSectors = () => setSectorFilter(new Set());
  const [stageFilter, setStageFilter] = useState("");
  const [hideLost, setHideLost] = useState(false);
  // Iter336 · Strategic filters
  const [dataFilters, setDataFilters] = useState({
    hasEmail: false, hasPhone: false, hasWebsite: false,
  });
  const [sizeFilter, setSizeFilter] = useState("all"); // all|small|mid|large
  const [tempFilter, setTempFilter] = useState("all"); // all|hot|warm|cold
  const [geoQuery, setGeoQuery] = useState("");
  const [geoCenter, setGeoCenter] = useState(null); // {lat,lng,label,radiusKm}
  const [radiusKm, setRadiusKm] = useState(10);
  const [geoBusy, setGeoBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    axios.get(`${API}/geocode/leads-map`, { params: { limit: 2000 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error(isPt ? "Falha ao carregar o mapa" : "Failed to load map"))
      .finally(() => setLoading(false));
    // Iter153 — pull the canonical sector list from Mongo so the filter
    // dropdown shows every segment (with counts) instead of the top-12
    // sample of currently-loaded pins.
    axios.get(`${API}/leads/sectors`, { params: { only_mapped: true } })
      .then(({ data }) => setSectorCatalog(data.sectors || []))
      .catch(() => { /* dropdown falls back to items-derived list */ });
    return () => { window.gm_authFailure = undefined; };
  }, []); // eslint-disable-line

  // Sector catalog for the UI: prefer the backend distinct list (all segments,
  // sorted alphabetically); fall back to items-derived list when the endpoint
  // hasn't answered yet.
  const sectors = useMemo(() => {
    if (sectorCatalog.length) return sectorCatalog.map((r) => r.sector);
    return [...new Set(items.map((x) => x.sector).filter(Boolean))].sort();
  }, [sectorCatalog, items]);
  const sectorCountByName = useMemo(() => {
    const map = new Map();
    sectorCatalog.forEach((r) => map.set(r.sector, r.count));
    return map;
  }, [sectorCatalog]);
  const stages = useMemo(
    () => [...new Set(items.map((x) => x.crm_status).filter(Boolean))], [items]);
  const colorFor = useCallback((sector, lead) => {
    // Gov pins get a dedicated amber tone regardless of trade category.
    if (lead && lead.kind === "gov") return GOV_COLOR;
    const idx = sectors.indexOf(sector);
    return SECTOR_COLORS[idx >= 0 ? idx % SECTOR_COLORS.length : 0];
  }, [sectors]);

  const visible = useMemo(() => items.filter((l) => {
    if (sectorFilter.size > 0 && !sectorFilter.has(l.sector)) return false;
    if (stageFilter && l.crm_status !== stageFilter) return false;
    if (hideLost && l.crm_status === "LOST") return false;
    if (geoCenter && geoCenter.radiusKm > 0 && distKm(geoCenter, l) > geoCenter.radiusKm) return false;
    // Iter336 · Data enrichment presence filters
    if (dataFilters.hasEmail && !l.email) return false;
    if (dataFilters.hasPhone && !l.phone) return false;
    if (dataFilters.hasWebsite && !(l.website || l.url)) return false;
    // Iter336 · Company size (proxy via digital_score when employees absent)
    if (sizeFilter !== "all") {
      const emp = l.employees ?? null;
      const proxy = l.digital_score ?? 0;
      const size = emp != null
        ? (emp < 10 ? "small" : emp < 50 ? "mid" : "large")
        : (proxy < 40 ? "small" : proxy < 70 ? "mid" : "large");
      if (size !== sizeFilter) return false;
    }
    // Iter336 · AI temperature score
    if (tempFilter !== "all") {
      const s = l.digital_score ?? 0;
      const t = s >= 70 ? "hot" : s >= 40 ? "warm" : "cold";
      if (t !== tempFilter) return false;
    }
    return true;
  }), [items, sectorFilter, stageFilter, hideLost, geoCenter,
        dataFilters, sizeFilter, tempFilter]);

  const routeIds = useMemo(() => new Set(route.map((r) => r.id)), [route]);
  const routePath = useMemo(() => route.map((r) => ({ lat: r.lat, lng: r.lng })), [route]);

  const toggleRoute = (lead) => {
    setRoute((r) => routeIds.has(lead.id) ? r.filter((x) => x.id !== lead.id) : [...r, lead]);
    if (!routeIds.has(lead.id)) toast.success(isPt ? `📍 ${lead.company_name || lead.name} na rota` : "Added to route", { duration: 1600 });
  };

  const optimize = () => {
    if (route.length < 2) return;
    setRoute(optimizeOrder(route, geoCenter || null));
    toast.success(isPt ? "🧠 Rota otimizada (vizinho mais próximo)" : "Route optimized");
  };

  /** Iter155 — CSV export for field crews.
   *  Column set mirrors what fleet software / dispatchers usually need:
   *  stop order, company, sector, kind (B2B|Gov), full address, city/state,
   *  phone, email, CRM status, lat/lng. UTF-8 BOM so Excel opens correctly.
   */
  const exportRouteCSV = () => {
    if (route.length === 0) return;
    const origin = window.location.origin;
    const header = [
      "stop", "kind", "company", "sector", "address", "city", "state",
      "phone", "email", "status", "lat", "lng", "source_url", "crm_url",
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = route.map((r, i) => {
      const crmUrl = r.kind === "gov"
        ? `${origin}/gov-kanban?highlight=${encodeURIComponent(r.id)}`
        : `${origin}/kanban?q=${encodeURIComponent(r.company_name || r.name || "")}`;
      return [
        i + 1, r.kind || "b2b", r.company_name || r.name || "",
        r.sector || "", r.address || "", r.city || "", r.state || "",
        r.phone || "", r.email || "", r.crm_status || "",
        r.lat, r.lng, r.source_url || "", crmUrl,
      ].map(esc).join(",");
    });
    const csv = "\uFEFF" + [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `agentelead-route-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(isPt ? "CSV baixado" : "CSV downloaded");
  };

  const navUrl = useMemo(() => {
    if (route.length === 0) return "";
    const pts = route.map((r) => `${r.lat},${r.lng}`);
    const dest = pts[pts.length - 1];
    const way = pts.slice(0, -1).slice(0, 9).join("|");
    return `https://www.google.com/maps/dir/?api=1&destination=${dest}${way ? `&waypoints=${encodeURIComponent(way)}` : ""}&travelmode=driving`;
  }, [route]);

  const geoSearch = async (e) => {
    e?.preventDefault?.();
    if (geoQuery.trim().length < 3) return;
    setGeoBusy(true);
    try {
      const { data } = await axios.get(`${API}/geocode/search`, { params: { q: geoQuery } });
      const r = (data.results || [])[0];
      if (r) { setGeoCenter({ ...r, radiusKm }); toast.success(`📍 ${r.label}`); }
      else toast.info(isPt ? "Endereço não encontrado" : "Address not found");
    } catch (_e) { toast.error("Geocode failed"); }
    finally { setGeoBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation unavailable");
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeoCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: isPt ? "Minha localização" : "My location", radiusKm }),
      () => toast.error(isPt ? "Permissão de localização negada" : "Location permission denied"));
  };

  useEffect(() => {
    if (geoCenter) setGeoCenter((c) => c && { ...c, radiusKm });
  }, [radiusKm]); // eslint-disable-line

  const isPremium = (l) => (l.digital_score ?? 0) >= 85;
  const sMeta = (st) => STATUS_META[st] || { pt: st || "—", en: st || "—", color: "#6B7280" };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="leads-map-page">
      {!focus && <Sidebar />}
      <main className={`${focus ? "" : "ml-[var(--al-sidebar-w,260px)]"} min-w-0 flex flex-col min-h-screen ${focus ? "p-0" : "px-4 py-4 lg:px-8 lg:py-6"}`}>
        {!focus && (
          <header className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div>
              <button type="button" onClick={() => navigate(-1)} data-testid="lm-back-btn"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[var(--al-text-muted)] hover:text-[#C9A24E] mb-1 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> {isPt ? "Voltar" : "Back"}
              </button>
              <h1 className="text-[20px] font-extrabold inline-flex items-center gap-2">
                <MapIcon className="w-5 h-5 text-[#C9A24E]" /> {isPt ? "Mapa de Leads" : "Leads Map"}
                <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-[#3B82F6]/10 text-[#3B82F6]">Google Maps</span>
              </h1>
              <p className="text-[12px] text-[var(--al-text-muted)]">
                {visible.length} {isPt ? "leads visíveis" : "visible leads"}{route.length > 0 && ` · ${route.length} ${isPt ? "na rota" : "in route"}`}
              </p>
            </div>
            <button type="button" onClick={() => setHelpOpen(true)} data-testid="lm-help-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-semibold hover:border-[#C9A24E] hover:text-[#C9A24E] transition-colors"
              title={isPt ? "Como dominar o Leads Map" : "How to master the Leads Map"}>
              <HelpCircle className="w-4 h-4" />
              {isPt ? "Como usar" : "How to use"}
            </button>
            <ExportButton
              source="leads-map"
              testidPrefix="lm-export"
              filters={{
                segment: sectorFilter.size === 1 ? Array.from(sectorFilter)[0] : "",
                status: stageFilter || "",
                center_lat: geoCenter?.lat,
                center_lng: geoCenter?.lng,
                // Convert km → mi so backend Haversine reads consistent units
                radius_miles: geoCenter && geoCenter.radiusKm > 0
                  ? (geoCenter.radiusKm * 0.621371).toFixed(2) : "",
              }}
            />
          </header>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-[var(--al-text-muted)]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className={`flex-1 relative overflow-hidden ${focus ? "" : "rounded-xl border border-[var(--al-border)]"}`}
            style={{ minHeight: focus ? "100dvh" : 560, isolation: "isolate" }} data-testid="lm-map">
            {(!GMAPS_KEY || mapError) ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center p-6">
                <MapIcon className="w-8 h-8 text-[var(--al-text-muted)]" />
                <p className="text-[13px] font-semibold">{isPt ? "Google Maps indisponível" : "Google Maps unavailable"}</p>
                <p className="text-[12px] text-[var(--al-text-muted)] max-w-sm">
                  {isPt
                    ? "Verifique se a chave possui a 'Maps JavaScript API' habilitada no Google Cloud Console."
                    : "Check that the key has 'Maps JavaScript API' enabled in Google Cloud Console."}
                </p>
              </div>
            ) : (
              <div className="absolute inset-0">
                <APIProvider apiKey={GMAPS_KEY}>
                  <GMap style={{ width: "100%", height: "100%" }}
                    defaultCenter={{ lat: 28.5384, lng: -81.3789 }} defaultZoom={7}
                    gestureHandling="greedy" streetViewControl={false} mapTypeControl={false} fullscreenControl={false}>
                    <MapContent leads={visible} colorFor={colorFor} onSelect={setSelected}
                      routeIds={routeIds} routePath={routePath} focusCenter={geoCenter} />
                  </GMap>
                </APIProvider>
              </div>
            )}

            {/* Focus mode toggle */}
            <button type="button" data-testid="lm-focus-toggle"
              onClick={() => setFocus((f) => !f)}
              title={focus ? (isPt ? "Sair do modo foco" : "Exit focus") : (isPt ? "Modo foco (tela cheia)" : "Focus mode")}
              className="absolute top-3 left-3 z-10 w-11 h-11 rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-lg flex items-center justify-center hover:border-[#C9A24E]">
              {focus ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            {focus && (
              <button type="button" onClick={() => setFocus(false)} data-testid="lm-focus-back"
                className="absolute top-3 left-16 z-10 h-11 px-3 rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-lg text-[12px] font-bold inline-flex items-center gap-1.5">
                <ArrowLeft className="w-3.5 h-3.5" /> {isPt ? "Voltar" : "Back"}
              </button>
            )}

            {/* Unified retractable control panel (top-right) */}
            <div className="absolute top-3 right-3 z-10 w-[280px] max-w-[calc(100%-24px)]" data-testid="lm-control-panel">
              <button type="button" data-testid="lm-panel-toggle"
                onClick={() => setPanelOpen((o) => !o)}
                className="w-full h-11 px-3.5 rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-lg flex items-center gap-2 text-[12.5px] font-bold">
                <SlidersHorizontal className="w-4 h-4 text-[#C9A24E]" />
                {isPt ? "Filtros & Legenda" : "Filters & Legend"}
                <span className="ml-auto">{panelOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
              </button>
              {panelOpen && (
                <div className="mt-2 rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-xl p-3.5 space-y-3 max-h-[62vh] overflow-y-auto">
                  {/* Multi-sector filter — checkboxes with counts */}
                  <div className="relative">
                    <button type="button" onClick={() => setSectorMenuOpen((v) => !v)}
                      className="al-input w-full h-9 text-[12px] flex items-center justify-between"
                      data-testid="lm-sector-filter">
                      <span className="truncate">
                        {sectorFilter.size === 0
                          ? `${isPt ? "Todos os setores" : "All sectors"} (${items.length})`
                          : `${sectorFilter.size} ${isPt ? "setores" : "sectors"} · ${visible.length}`}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                    </button>
                    {sectorMenuOpen && !isMobile && (
                      <div className="absolute z-40 mt-1 left-0 right-0 max-h-72 overflow-y-auto rounded-lg border border-[var(--al-border)] shadow-2xl p-1.5"
                           style={{ backgroundColor: "var(--al-card-bg, #FFFFFF)" }}
                           data-testid="lm-sector-menu">
                        <LmSectorList sectors={sectors}
                          sectorFilter={sectorFilter} toggleSector={toggleSector}
                          sectorCountByName={sectorCountByName}
                          clearSectors={clearSectors} colorFor={colorFor} isPt={isPt} />
                      </div>
                    )}
                    <BottomSheet
                      open={sectorMenuOpen && isMobile}
                      onClose={() => setSectorMenuOpen(false)}
                      title={isPt ? "Setores" : "Sectors"}
                      subtitle={sectorFilter.size === 0
                        ? (isPt ? "Todos" : "All")
                        : `${sectorFilter.size}/${sectors.length}`}
                      doneLabel={isPt ? "Concluir" : "Done"}
                      testid="lm-sector-sheet"
                    >
                      <div className="p-2">
                        <LmSectorList sectors={sectors}
                          sectorFilter={sectorFilter} toggleSector={toggleSector}
                          sectorCountByName={sectorCountByName}
                          clearSectors={clearSectors} colorFor={colorFor}
                          isPt={isPt} mobile />
                      </div>
                    </BottomSheet>
                  </div>
                  {/* Interactive legend — same set as dropdown but always visible */}
                  <div className="flex flex-wrap gap-1.5" data-testid="lm-legend">
                    {/* Gov swatch — always shown when there are gov pins */}
                    {items.some((i) => i.kind === "gov") && (
                      <span data-testid="lm-legend-gov"
                        className="text-[10px] px-2 py-1 rounded-full border border-[#F59E0B]/50 bg-[#F59E0B]/10 inline-flex items-center gap-1 font-bold text-[#B45309]">
                        <span className="inline-block w-2 h-2 rotate-45" style={{ background: GOV_COLOR }} />
                        Gov
                        <span className="text-[9px] font-semibold text-[var(--al-text-muted)]">
                          {items.filter((i) => i.kind === "gov").length}
                        </span>
                      </span>
                    )}
                    {sectors.map((s) => {
                      const n = sectorCountByName.get(s);
                      const active = sectorFilter.has(s);
                      return (
                        <button key={s} type="button" onClick={() => toggleSector(s)}
                          data-testid={`lm-legend-${s}`}
                          className={`text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1 transition ${
                            active ? "border-[#C9A24E] bg-[#C9A24E]/10 font-bold" : "border-[var(--al-border)] hover:border-[#C9A24E]/50"}`}>
                          <span className="w-2 h-2 rounded-full" style={{ background: colorFor(s) }} />
                          {s}
                          {n != null && (
                            <span className="text-[9px] font-semibold text-[var(--al-text-muted)]">{n}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* CRM filters */}
                  <div className="pt-2 border-t border-[var(--al-border)] space-y-2">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-[var(--al-text-muted)]">CRM</div>
                    <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                      className="al-input w-full h-9 text-[12px]" data-testid="lm-stage-filter">
                      <option value="">{isPt ? "Todas as fases do funil" : "All funnel stages"}</option>
                      {stages.map((st) => <option key={st} value={st}>{isPt ? sMeta(st).pt : sMeta(st).en}</option>)}
                    </select>
                    <label className="flex items-center gap-2 text-[12px] cursor-pointer" data-testid="lm-hide-lost">
                      <input type="checkbox" checked={hideLost} onChange={(e) => setHideLost(e.target.checked)}
                        className="accent-[#C9A24E] w-4 h-4" />
                      {isPt ? "Ocultar leads perdidos" : "Hide lost leads"}
                    </label>
                  </div>
                  {/* Iter336 · Enrichment + size + temperature filters */}
                  <div className="pt-2 border-t border-[var(--al-border)] space-y-2" data-testid="lm-strategic-filters">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-[var(--al-text-muted)]">
                      {isPt ? "Filtros estratégicos" : "Strategic filters"}
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="lm-enrich-filters">
                      {[
                        { k: "hasEmail",   label: isPt ? "✉ E-mail"    : "✉ Email"   },
                        { k: "hasPhone",   label: isPt ? "☎ Telefone" : "☎ Phone"   },
                        { k: "hasWebsite", label: isPt ? "🌐 Site"     : "🌐 Website" },
                      ].map((f) => {
                        const on = dataFilters[f.k];
                        return (
                          <button key={f.k} type="button"
                            data-testid={`lm-enrich-${f.k}`}
                            onClick={() => setDataFilters((p) => ({ ...p, [f.k]: !p[f.k] }))}
                            className={`h-7 px-2 rounded-full text-[10.5px] font-bold border transition ${
                              on ? "bg-emerald-500 text-white border-emerald-500"
                                 : "border-[var(--al-border)] hover:border-emerald-500 text-[var(--al-text-muted)]"}`}>
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <select value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}
                        className="al-input h-8 text-[11.5px]" data-testid="lm-size-filter">
                        <option value="all">{isPt ? "Todos os portes" : "All sizes"}</option>
                        <option value="small">{isPt ? "Pequena" : "Small"}</option>
                        <option value="mid">{isPt ? "Média" : "Mid"}</option>
                        <option value="large">{isPt ? "Grande" : "Large"}</option>
                      </select>
                      <select value={tempFilter} onChange={(e) => setTempFilter(e.target.value)}
                        className="al-input h-8 text-[11.5px]" data-testid="lm-temp-filter">
                        <option value="all">{isPt ? "Todas as temperaturas" : "All temperatures"}</option>
                        <option value="hot">🔥 {isPt ? "Quente" : "Hot"} (≥70)</option>
                        <option value="warm">☀ {isPt ? "Morno" : "Warm"} (40-69)</option>
                        <option value="cold">❄ {isPt ? "Frio" : "Cold"} (&lt;40)</option>
                      </select>
                    </div>
                  </div>

                  {/* Geo-filter */}
                  <div className="pt-2 border-t border-[var(--al-border)] space-y-2">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-[var(--al-text-muted)]">
                      {isPt ? "Filtro por raio" : "Radius filter"}
                    </div>
                    <form onSubmit={geoSearch} className="flex gap-1.5">
                      <input value={geoQuery} onChange={(e) => setGeoQuery(e.target.value)}
                        placeholder={isPt ? "Endereço ou cidade…" : "Address or city…"}
                        className="al-input flex-1 h-9 px-2.5 text-[12px] min-w-0" data-testid="lm-geo-input" />
                      <button type="submit" disabled={geoBusy} data-testid="lm-geo-search"
                        className="h-9 w-9 shrink-0 rounded-lg bg-[#1B3A8A] text-white flex items-center justify-center disabled:opacity-50">
                        {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      </button>
                      <button type="button" onClick={useMyLocation} data-testid="lm-geo-mylocation"
                        title={isPt ? "Usar minha localização" : "Use my location"}
                        className="h-9 w-9 shrink-0 rounded-lg border border-[var(--al-border)] flex items-center justify-center hover:border-[#C9A24E]">
                        <Crosshair className="w-3.5 h-3.5" />
                      </button>
                    </form>
                    <div className="flex gap-1.5">
                      {RADIUS_MILES.map((mi) => {
                        const km = mi * MILE_TO_KM;
                        const active = mi === 0
                          ? radiusKm === 0
                          : Math.abs(radiusKm - km) < 0.01;
                        return (
                          <button key={mi} type="button" onClick={() => setRadiusKm(km)}
                            data-testid={`lm-radius-${mi}`}
                            className={`flex-1 h-8 rounded-lg border text-[11px] font-bold ${
                              active ? "bg-[#C9A24E] text-[#0A1233] border-[#C9A24E]" : "border-[var(--al-border)]"}`}>
                            {mi === 0 ? "Off" : `${mi}mi`}
                          </button>
                        );
                      })}
                    </div>
                    {geoCenter && (
                      <div className="flex items-center gap-1.5 text-[11px] text-[var(--al-text-muted)]">
                        <MapPin className="w-3 h-3 text-[#C9A24E]" />
                        <span className="truncate flex-1">{geoCenter.label}</span>
                        <button type="button" onClick={() => setGeoCenter(null)} data-testid="lm-geo-clear">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Lead preview card (bottom-left) */}
            {selected && (
              <div className="absolute bottom-3 left-3 z-10 w-[320px] max-w-[calc(100%-24px)] rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-2xl p-4"
                data-testid="lm-preview-card">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-extrabold leading-snug break-words flex items-center gap-1.5">
                      {selected.kind === "gov" && (
                        <Landmark className="w-3.5 h-3.5 text-[#F59E0B] shrink-0" />
                      )}
                      <span className="min-w-0 truncate">{selected.company_name || selected.name}</span>
                      {isPremium(selected) && onTrial && (
                        <button type="button" onClick={() => navigate("/billing")}
                          data-testid="lm-premium-lock"
                          title={isPt ? "Desbloquear informações completas" : "Unlock full details"}
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#C9A24E]/15 text-[#92660C] align-middle">
                          <Lock className="w-2.5 h-2.5" /> Premium
                        </button>
                      )}
                    </div>
                    {/* Gov gets a "title" line, B2B gets the sector/city chip line */}
                    {selected.kind === "gov" ? (
                      <div className="text-[10.5px] text-[var(--al-text-muted)] mt-0.5 line-clamp-2">
                        {selected.name}
                      </div>
                    ) : null}
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: `${sMeta(selected.crm_status).color}1D`, color: sMeta(selected.crm_status).color }}
                        data-testid="lm-preview-status">
                        {selected.kind === "gov"
                          ? (selected.crm_status || "NEW")
                          : (isPt ? sMeta(selected.crm_status).pt : sMeta(selected.crm_status).en)}
                      </span>
                      <span className="text-[10.5px] text-[var(--al-text-muted)] truncate">
                        {selected.sector || "—"} · {selected.city || ""}{selected.state ? `, ${selected.state}` : ""}
                      </span>
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelected(null)} data-testid="lm-preview-close"
                    className="shrink-0 text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Contact / value strip */}
                <div className="mt-3 space-y-1.5">
                  {selected.kind === "gov" && selected.estimated_value > 0 && (
                    <div className="flex items-center gap-1.5 text-[11.5px]" data-testid="lm-preview-value">
                      <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="font-bold text-emerald-600">
                        US$ {Number(selected.estimated_value).toLocaleString("en-US")}
                      </span>
                      <span className="text-[10.5px] text-[var(--al-text-muted)]">
                        {isPt ? "· valor estimado" : "· estimated value"}
                      </span>
                    </div>
                  )}
                  {selected.email && (
                    <a href={`mailto:${selected.email}`} data-testid="lm-preview-email"
                      className="inline-flex items-center gap-1.5 text-[11.5px] text-[#3B82F6] hover:text-[#1E40AF] hover:underline break-all">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      {selected.email}
                    </a>
                  )}
                </div>

                <div className="flex gap-2 mt-3">
                  <button type="button" data-testid="lm-preview-quickaction"
                    onClick={() => setQuickAction(selected)}
                    className="flex-1 h-10 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    {isPt ? "Ações rápidas" : "Quick actions"}
                  </button>
                  <button type="button" data-testid="lm-preview-addroute"
                    onClick={() => toggleRoute(selected)}
                    className={`flex-1 h-10 rounded-lg text-[11.5px] font-bold ${
                      routeIds.has(selected.id)
                        ? "bg-rose-50 text-rose-600 border border-rose-200"
                        : "bg-[#1B3A8A] text-white"}`}>
                    {routeIds.has(selected.id)
                      ? (isPt ? "Remover da Rota" : "Remove from Route")
                      : (isPt ? "+ Rota de Hoje" : "+ Today's Route")}
                  </button>
                </div>
                {/* Iter336 · secondary link to full CRM view */}
                <button type="button" data-testid="lm-preview-profile"
                  onClick={() => navigate(
                    selected.kind === "gov"
                      ? `/gov-kanban?highlight=${encodeURIComponent(selected.id)}`
                      : `/kanban?q=${encodeURIComponent(selected.company_name || selected.name || "")}`,
                  )}
                  className="w-full mt-2 h-8 text-[11px] font-semibold text-[#1B3A8A] hover:underline inline-flex items-center justify-center gap-1.5">
                  <ExternalLink className="w-3 h-3" />
                  {isPt ? "Ver perfil completo no CRM" : "See full CRM profile"}
                </button>
              </div>
            )}

            {/* Route builder panel (bottom-right) */}
            {route.length > 0 && (
              <div className="absolute bottom-3 right-3 z-10 w-[280px] max-w-[calc(100%-24px)] rounded-xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-2xl overflow-hidden"
                data-testid="lm-route-panel">
                <div className="px-3.5 py-2.5 bg-[#1B3A8A] text-white flex items-center gap-2">
                  <RouteIcon className="w-4 h-4" />
                  <span className="text-[12px] font-bold flex-1">{isPt ? "Rota de Campo" : "Field Route"} ({route.length})</span>
                  <button type="button" onClick={() => setRoute([])} data-testid="lm-route-clear" title={isPt ? "Limpar" : "Clear"}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="max-h-[160px] overflow-y-auto divide-y divide-[var(--al-border)]">
                  {route.map((r, i) => {
                    const crmHref = r.kind === "gov"
                      ? `/gov-kanban?highlight=${encodeURIComponent(r.id)}`
                      : `/kanban?q=${encodeURIComponent(r.company_name || r.name || "")}`;
                    return (
                      <div key={r.id} className="px-3 py-1.5 flex items-center gap-2 text-[11.5px]" data-testid={`lm-route-stop-${i}`}>
                        <span className="w-5 h-5 rounded-full bg-[#C9A24E]/15 text-[#92660C] text-[10px] font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span className="truncate flex-1">{r.company_name || r.name}</span>
                        <a href={crmHref}
                          data-testid={`lm-route-crm-${i}`}
                          onClick={(e) => { e.preventDefault(); navigate(crmHref); }}
                          title={isPt ? "Ver no CRM" : "See in CRM"}
                          className="text-[var(--al-text-muted)] hover:text-[#1B3A8A] shrink-0">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <button type="button" onClick={() => toggleRoute(r)}
                          title={isPt ? "Remover" : "Remove"}
                          className="text-[var(--al-text-muted)] hover:text-rose-500">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="p-2.5 space-y-1.5">
                  <button type="button" onClick={optimize} disabled={route.length < 2}
                    data-testid="lm-route-optimize"
                    className="w-full h-10 rounded-lg border border-[#8B5CF6] text-[#8B5CF6] text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-40">
                    <Sparkles className="w-3.5 h-3.5" /> {isPt ? "Gerar Melhor Rota" : "Optimize Route"}
                  </button>
                  <a href={navUrl} target="_blank" rel="noopener noreferrer"
                    data-testid="lm-route-navigate"
                    className="w-full h-10 rounded-lg bg-[#10B981] text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1.5">
                    <Navigation className="w-3.5 h-3.5" /> {isPt ? "Navegar Agora (Google Maps)" : "Navigate Now (Google Maps)"}
                  </a>
                  <button type="button" onClick={exportRouteCSV} data-testid="lm-route-csv"
                    className="w-full h-9 rounded-lg border border-[var(--al-border)] text-[11px] font-bold text-[var(--al-text-muted)] hover:text-[var(--al-text)] hover:border-[#C9A24E] inline-flex items-center justify-center gap-1.5 transition-colors">
                    <Download className="w-3.5 h-3.5" /> {isPt ? "Baixar CSV" : "Download CSV"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Help modal — didactic guide for the Leads Map */}
      {helpOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
             onClick={() => setHelpOpen(false)}
             data-testid="lm-help-modal-backdrop">
          <div className="relative w-full max-w-2xl rounded-2xl bg-[var(--al-card-bg)] border border-[var(--al-border)] shadow-2xl overflow-hidden"
               onClick={(e) => e.stopPropagation()}
               data-testid="lm-help-modal">
            <div className="px-6 py-4 border-b border-[var(--al-border)] flex items-center gap-2.5 bg-gradient-to-r from-[#C9A24E]/15 to-transparent">
              <HelpCircle className="w-5 h-5 text-[#C9A24E]" />
              <h2 className="text-[17px] font-extrabold flex-1">
                {isPt ? "Como dominar o Leads Map" : "How to master the Leads Map"}
              </h2>
              <button type="button" onClick={() => setHelpOpen(false)}
                data-testid="lm-help-close"
                className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto text-[13.5px] leading-relaxed">
              <section data-testid="lm-help-sources">
                <div className="flex items-center gap-2 mb-1.5">
                  <MapPin className="w-4 h-4 text-[#3B82F6]" />
                  <h3 className="font-bold">
                    {isPt ? "De onde vêm os leads" : "Where the leads come from"}
                  </h3>
                </div>
                <p className="text-[var(--al-text-muted)]">
                  {isPt
                    ? "O mapa reflete automaticamente as buscas feitas em Lead Discovery, Scrapers (Instagram/LinkedIn/YouTube/Yelp) e Obras Governamentais (SAM.gov / Public Works). Certifique-se de que os leads tenham endereços válidos — o app roda um geocode automático a cada captura para criar os pins."
                    : "The map auto-syncs with searches from Lead Discovery, Scrapers (Instagram/LinkedIn/YouTube/Yelp) and Government Contracts (SAM.gov / Public Works). Make sure your leads carry valid addresses — the app auto-geocodes each new capture to drop the pin."}
                </p>
              </section>

              <section data-testid="lm-help-radius">
                <div className="flex items-center gap-2 mb-1.5">
                  <Crosshair className="w-4 h-4 text-[#C9A24E]" />
                  <h3 className="font-bold">
                    {isPt ? "Estratégia · Filtro de Raio" : "Strategy · Radius Filter"}
                  </h3>
                </div>
                <p className="text-[var(--al-text-muted)]">
                  {isPt
                    ? "Use o filtro de raio para planejar rotas de visitas. Concentre esforços em leads a menos de 10km ou 25km da sua base — reduz custo de frete, tempo de deslocamento e aumenta o número de reuniões por dia."
                    : "Use the radius filter to plan field visits. Focus on leads within 10 km or 25 km of your base — cuts travel cost, saves hours and multiplies daily meetings."}
                </p>
              </section>

              <section data-testid="lm-help-hotzones">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-4 h-4 text-[#EF4444]" />
                  <h3 className="font-bold">
                    {isPt ? "Estratégia · Zonas Quentes" : "Strategy · Hot Zones"}
                  </h3>
                </div>
                <p className="text-[var(--al-text-muted)]">
                  {isPt
                    ? "Pins densamente agrupados indicam áreas de alta atividade comercial ou de obras públicas. São as melhores regiões para focar campanhas de marketing local, panfletagem, ligações a frio e visitas físicas."
                    : "Densely clustered pins mark high-activity commercial or public-works zones. These are the top regions to focus local marketing, cold calling and door-to-door visits."}
                </p>
              </section>

              <section data-testid="lm-help-action">
                <div className="flex items-center gap-2 mb-1.5">
                  <ExternalLink className="w-4 h-4 text-[#10B981]" />
                  <h3 className="font-bold">
                    {isPt ? "Ação imediata" : "Immediate action"}
                  </h3>
                </div>
                <p className="text-[var(--al-text-muted)]">
                  {isPt
                    ? "Clique em qualquer pin para abrir o card com nome da empresa, e-mail (mailto:), valor estimado (se for licitação) e o botão \"Ver no CRM\" — você move o lead direto no Kanban sem sair do mapa. Adicione até 20 paradas na \"Rota de Hoje\" e abra no Google Maps com um toque."
                    : "Click any pin to open the card with company name, mailto: email, estimated value (for Gov bids) and a \"See in CRM\" button — move the lead straight in the Kanban without leaving the map. Add up to 20 stops to \"Today's Route\" and launch Google Maps in one tap."}
                </p>
              </section>
            </div>
            <div className="px-6 py-3 border-t border-[var(--al-border)] flex justify-end bg-[var(--al-bg)]">
              <button type="button" onClick={() => setHelpOpen(false)}
                data-testid="lm-help-got-it"
                className="h-9 px-4 rounded-lg bg-[#C9A24E] text-[#0A1233] text-[12.5px] font-bold hover:bg-[#B08F3E]">
                {isPt ? "Entendi" : "Got it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter336 · Quick Action Panel */}
      {quickAction && (
        <QuickActionPanel
          lead={quickAction}
          isPt={isPt}
          onClose={() => setQuickAction(null)}
        />
      )}
    </div>
  );
}
// the mobile bottom sheet for LeadsMap.
// Iter336 · Added inline search input for quick filtering across
// hundreds of segments.
function LmSectorList({ sectors, sectorFilter, toggleSector, sectorCountByName,
                          clearSectors, colorFor, isPt, mobile = false }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const filtered = query
    ? sectors.filter((s) => s.toLowerCase().includes(query))
    : sectors;
  return (
    <>
      <div className="px-2 pt-1.5 pb-1 border-b border-[var(--al-border)] mb-1 space-y-1.5">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="lm-sector-search"
          placeholder={isPt ? "Buscar setor…" : "Search sector…"}
          className={`al-input w-full ${mobile ? "h-9 text-[16px]" : "h-7 text-[11.5px]"}`} />
        <div className="flex items-center justify-between">
          <button type="button" onClick={clearSectors} data-testid="lm-sector-clear"
            className="text-[12px] font-semibold text-[#C9A24E] hover:underline disabled:opacity-40"
            disabled={sectorFilter.size === 0}>
            {isPt ? "Limpar" : "Clear"}
          </button>
          <span className="text-[10.5px] text-[var(--al-text-muted)]">
            {sectorFilter.size}/{sectors.length}
          </span>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center text-[11px] text-[var(--al-text-muted)] py-4">
          {isPt ? "Nenhum resultado" : "No matches"}
        </div>
      ) : filtered.map((s) => {
        const n = sectorCountByName.get(s);
        const checked = sectorFilter.has(s);
        return (
          <label key={s} data-testid={`lm-sector-option-${s}`}
            className={`flex items-center gap-2 px-2 ${mobile ? "py-3 min-h-[44px] text-[14px]" : "py-1.5 text-[12px]"} rounded-md cursor-pointer hover:bg-[var(--al-bg)] ${checked ? "bg-[#C9A24E]/10" : ""}`}>
            <input type="checkbox" checked={checked}
              onChange={() => toggleSector(s)}
              className={`accent-[#C9A24E] ${mobile ? "w-4 h-4" : ""}`} />
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorFor(s) }} />
            <span className="flex-1 truncate">{s}</span>
            {n != null && (
              <span className="text-[10.5px] text-[var(--al-text-muted)]">{n}</span>
            )}
          </label>
        );
      })}
    </>
  );
}
